import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  CreditCard as CreditCardIcon,
  ConfirmationNumber as TicketIcon,
  EmojiEvents as TrophyIcon,
  AccountCircle as AccountIcon,
  ShoppingCart as OrderIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const MemberDetailDialog = ({ open, onClose, member }) => {
  if (!member) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return "N/A";
    return format(new Date(date), "dd/MM/yyyy");
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
      case "cancelled":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const customerData = member.customer_data || {};
  const date_created = member.date_created || {};
  const orderData = member.order_data || {};
  const giveawayWins = member.giveawaysWonData || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6">Member Details</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AccountIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">Personal Information</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    src={customerData.avatar_url}
                    alt={`${customerData.first_name} ${customerData.last_name}`}
                    sx={{ width: 48, height: 48, mr: 2 }}
                  >
                    {customerData.first_name?.[0]}
                    {customerData.last_name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {customerData.first_name} {customerData.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Member ID: {member.id}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AccountIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    <strong>Username:</strong> {customerData.username || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <EmailIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    <strong>Email:</strong> {customerData.email}
                  </Typography>
                </Box>

                {customerData.phone && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PhoneIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography variant="body2">
                      <strong>Phone:</strong> {customerData.phone}
                    </Typography>
                  </Box>
                )}

                {(customerData.billing_address_1 ||
                  customerData.shipping_address_1) && (
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                  >
                    <LocationIcon
                      fontSize="small"
                      sx={{ mr: 1, mt: 0.2, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="body2">
                        <strong>Address:</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {customerData.billing_address_1 ||
                          customerData.shipping_address_1}
                        <br />
                        {customerData.billing_city ||
                          customerData.shipping_city}
                        ,{" "}
                        {customerData.billing_state ||
                          customerData.shipping_state}{" "}
                        {customerData.billing_postcode ||
                          customerData.shipping_postcode}
                        <br />
                        {customerData.billing_country ||
                          customerData.shipping_country}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CalendarIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    <strong>Member Since:</strong> {formatDate(date_created)}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={
                      member.status?.charAt(0).toUpperCase() +
                        member.status?.slice(1) || "Active"
                    }
                    color={getStatusColor(member.status)}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Membership & Order Information */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <OrderIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6">Membership</Typography>
                </Box>

                {orderData.id && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Latest Order
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        Order #{orderData.id}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Tickets
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TicketIcon
                      fontSize="small"
                      sx={{ mr: 0.5, color: "primary.main" }}
                    />
                    <Typography variant="h6" color="primary.main">
                      {member.total_entries || 0}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Giveaways Won
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TrophyIcon
                      fontSize="small"
                      sx={{ mr: 0.5, color: "warning.main" }}
                    />
                    <Typography variant="h6" color="warning.main">
                      {member.giveawaysWon || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Giveaway Wins */}
          {giveawayWins.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <TrophyIcon sx={{ mr: 1, color: "warning.main" }} />
                    <Typography variant="h6">Giveaway Wins</Typography>
                  </Box>

                  <List dense>
                    {giveawayWins.map((win, index) => (
                      <ListItem key={index} divider>
                        <ListItemIcon>
                          <TrophyIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={win.giveaway_title || "Unknown Giveaway"}
                          secondary={`Won on: ${formatDate(
                            win.win_date
                          )} - Status: ${win.status || "Claimed"}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberDetailDialog;
